import dayjs from 'dayjs';

/**
 * Calcule une plage de dates étendue en fonction de la vue du calendrier
 * @param date Date de référence
 * @param view Vue du calendrier (month, week, day, agenda)
 * @returns Plage de dates étendue (start, end)
 */
export function calculateDateRangeForView(
  date: Date,
  view: 'month' | 'week' | 'day' | 'agenda'
): { start: Date; end: Date } {
  let startDate = new Date(date);
  let endDate = new Date(date);

  if (view === 'month') {
    // Pour la vue mois, étendre la plage pour inclure les jours partiels
    // du mois précédent et du mois suivant qui sont affichés dans la grille

    // Ajuster pour inclure les semaines partielles
    // Début : premier jour de la semaine du premier jour du mois
    startDate = dayjs(startDate).startOf('month').startOf('week').toDate();
    // Fin : dernier jour de la semaine du dernier jour du mois
    endDate = dayjs(endDate).endOf('month').endOf('week').toDate();
  } else if (view === 'week') {
    // Pour la vue semaine, étendre d'une semaine avant et après
    startDate = dayjs(date).startOf('week').subtract(1, 'week').toDate();
    endDate = dayjs(date).endOf('week').add(1, 'week').toDate();
  } else if (view === 'day') {
    // Pour la vue jour, étendre de quelques jours avant et après
    startDate = dayjs(date).subtract(3, 'day').startOf('day').toDate();
    endDate = dayjs(date).add(3, 'day').endOf('day').toDate();
  } else if (view === 'agenda') {
    // Pour la vue agenda, utiliser une plage plus large
    startDate = dayjs(date).subtract(1, 'month').startOf('day').toDate();
    endDate = dayjs(date).add(1, 'month').endOf('day').toDate();
  }

  return { start: startDate, end: endDate };
}

/**
 * Extrait une date à partir d'une plage de dates (tableau ou objet)
 * @param range Plage de dates
 * @returns Date extraite
 */
export function getDateFromRange(range: Date[] | { start: Date; end: Date }): Date {
  if (Array.isArray(range)) {
    return range[0];
  }
  return range.start;
}

/**
 * Vérifie si deux dates sont le même jour
 * @param date1 Première date
 * @param date2 Deuxième date
 * @returns true si les dates sont le même jour
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Vérifie si deux plages de dates sont égales
 * @param range1 Première plage
 * @param range2 Deuxième plage
 * @returns true si les plages sont égales
 */
export function areDateRangesEqual(
  range1: { start: Date; end: Date; view: string },
  range2: { start: Date; end: Date; view: string }
): boolean {
  if (range1.view !== range2.view) {
    return false;
  }

  // Comparer les dates au jour près
  return isSameDay(range1.start, range2.start) && isSameDay(range1.end, range2.end);
}
